import { useContext, useState } from "react";
import QrScanner from "../../components/QrScanner";
import { useConstants } from "../../hooks/UseConstants";
import AuthContext from "../../context/AuthContext";
import { useWaits } from "../../hooks/UseWait";
import { Avatar, Box, Button, CircularProgress, Divider, TextField, Typography } from "@mui/material";
import Fetch from "../../services/Fetch";
import { FormattedMessage } from "react-intl";
import useSnackBar from "../../hooks/UseSnackBar";
import SnackbarAlert from "../../components/SnackBar";
import AdminPageHero from "../../components/AdminPageHero";
import PageContent from "../../components/PageContent";
import DashboardPanelCard from "../../components/cards/DashboardPanelCard";
import ScrollReveal from "../../components/motion/ScrollReveal";
import QrCodeScannerOutlinedIcon from '@mui/icons-material/QrCodeScannerOutlined';
import { formatCurrency } from "../../helper/formatCurrency";

function StudentWalletTopup() {
    const { wait } = useContext(AuthContext);
    const { host, language } = useConstants();
    const isAr = language === 'ar';
    const { sendWait, setSendWait } = useWaits();
    const { openSnackBar, type, message, setSnackBar, setOpenSnackBar } = useSnackBar();
    const [studentId, setStudentId] = useState('');
    const [amount, setAmount] = useState('');
    const [preview, setPreview] = useState(null);
    const [lastResult, setLastResult] = useState(null);
    const [scanOpen, setScanOpen] = useState(false);

    const parseStudentId = (raw) => {
        const v = String(raw ?? '').trim();
        if (!v) return '';

        const prefixed = v.match(/student\s*:\s*(\d+)/i);
        if (prefixed) return prefixed[1];

        if (/^\d+$/.test(v)) return v;

        const labeled = v.match(/(?:id|معرف(?:\s+الحساب)?)[^0-9]*(\d+)/i);
        if (labeled) return labeled[1];

        const trailing = v.match(/(\d+)(?!.*\d)/);
        return trailing ? trailing[1] : v;
    };

    const loadPreview = async (raw) => {
        const id = parseStudentId(raw ?? studentId);
        if (!id) return;
        setStudentId(id);
        const result = await Fetch(host + `/admin/users/${id}/show`, 'GET', null);
        if (result.status === 200) {
            setPreview(result.data.data);
        } else {
            setPreview(null);
            setSnackBar('error', isAr ? 'لم يُعثر على الطالب' : 'Student not found');
        }
    };

    const charge = async () => {
        const id = parseStudentId(studentId);
        if (!id || !amount) {
            setSnackBar('error', isAr ? 'أدخل معرف الطالب والمبلغ' : 'Enter student ID and amount');
            return;
        }
        setStudentId(id);
        setSendWait(true);
        const formData = new FormData();
        formData.append('amount', amount);
        const result = await Fetch(host + `/admin/wallet/charge/student/${id}`, 'POST', formData);
        if (result.status === 200) {
            setLastResult(result.data.data);
            setSnackBar('success', isAr ? 'تم شحن محفظة الطالب' : 'Student wallet topped up');
            setAmount('');
            await loadPreview(id);
        } else {
            setSnackBar('error', result.data?.errors?.[0] || result.data?.message || (isAr ? 'فشل الشحن' : 'Top-up failed'));
        }
        setSendWait(false);
    };

    return (
        <>
            <PageContent wait={wait} loading={false}>
                <AdminPageHero />

                <ScrollReveal>
                    <Box sx={{ maxWidth: 520, mx: 'auto' }}>
                        <DashboardPanelCard
                            title={isAr ? 'تفاصيل الشحن' : 'Top-up details'}
                            subtitle={isAr ? 'امسح QR أو أدخل معرف الطالب ثم حدّد المبلغ' : 'Scan QR or enter student ID, then set the amount'}
                            className="mx-0"
                        >
                            {scanOpen ? (
                                <Box sx={{ mb: 2 }}>
                                    <QrScanner
                                        onScan={(text) => {
                                            setStudentId(parseStudentId(text));
                                            setScanOpen(false);
                                            loadPreview(text);
                                        }}
                                        onClose={() => setScanOpen(false)}
                                    />
                                </Box>
                            ) : (
                                <Button
                                    variant="outlined"
                                    fullWidth
                                    startIcon={<QrCodeScannerOutlinedIcon />}
                                    onClick={() => setScanOpen(true)}
                                    sx={{ mb: 2, py: 1.25, borderRadius: 2, fontWeight: 700 }}
                                >
                                    {isAr ? 'مسح QR بالكاميرا' : 'Scan QR with camera'}
                                </Button>
                            )}

                            <TextField
                                fullWidth
                                label={isAr ? 'معرف الطالب / QR' : 'Student ID / QR'}
                                value={studentId}
                                onChange={(e) => { setStudentId(e.target.value); setPreview(null); }}
                                onBlur={() => loadPreview()}
                                sx={{ mb: 2 }}
                            />

                            {preview && (
                                <Box
                                    sx={{
                                        mb: 2,
                                        p: 2,
                                        borderRadius: 2,
                                        border: '1px solid',
                                        borderColor: 'divider',
                                        bgcolor: 'action.hover',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 2,
                                    }}
                                >
                                    <Avatar src={preview.image} sx={{ bgcolor: '#0D358C' }}>
                                        {preview.first_name?.charAt(0)}
                                    </Avatar>
                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                        <Typography variant="subtitle2" fontWeight={700} noWrap>
                                            {preview.first_name} {preview.last_name}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary" display="block" noWrap>
                                            {preview.email}
                                        </Typography>
                                        <Typography variant="body2" sx={{ color: '#0D358C', fontWeight: 700, mt: 0.5 }}>
                                            {isAr ? 'الرصيد الحالي:' : 'Current balance:'} {formatCurrency(preview.wallet?.balance ?? 0, isAr)}
                                        </Typography>
                                    </Box>
                                </Box>
                            )}

                            <Divider sx={{ my: 2 }} />

                            <TextField
                                fullWidth
                                label={<FormattedMessage id="amount" />}
                                type="number"
                                value={amount}
                                onChange={(e) => setAmount(e.target.value)}
                                inputProps={{ min: 0, step: '0.01' }}
                                sx={{ mb: 3 }}
                            />

                            <Button
                                fullWidth
                                variant="contained"
                                size="large"
                                onClick={charge}
                                disabled={sendWait || !studentId.trim() || !amount}
                                sx={{
                                    py: 1.35,
                                    fontWeight: 700,
                                    borderRadius: 2,
                                    background: 'linear-gradient(135deg, #0D358C 0%, #2563EB 100%)',
                                }}
                            >
                                {sendWait ? (
                                    <CircularProgress size={22} color="inherit" />
                                ) : (
                                    isAr ? 'تأكيد الشحن' : 'Confirm top-up'
                                )}
                            </Button>

                            {lastResult && (
                                <Box
                                    sx={{
                                        mt: 3,
                                        p: 2,
                                        borderRadius: 2,
                                        bgcolor: 'success.light',
                                        color: 'success.contrastText',
                                    }}
                                >
                                    <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                                        {isAr ? 'تم الشحن بنجاح' : 'Top-up successful'}
                                    </Typography>
                                    <Typography variant="body2">
                                        {isAr ? 'الطالب:' : 'Student:'} {lastResult.student_name}
                                    </Typography>
                                    <Typography variant="body2">
                                        {isAr ? 'المبلغ:' : 'Amount:'} {formatCurrency(lastResult.amount, isAr)}
                                    </Typography>
                                    <Typography variant="body2">
                                        {isAr ? 'الرصيد الجديد:' : 'New balance:'} {formatCurrency(lastResult.balance, isAr)}
                                    </Typography>
                                </Box>
                            )}
                        </DashboardPanelCard>
                    </Box>
                </ScrollReveal>
            </PageContent>

            <SnackbarAlert open={openSnackBar} message={message} severity={type} onClose={() => setOpenSnackBar(false)} />
        </>
    );
}

export default StudentWalletTopup;
